import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, Phone, IndianRupee, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "@/lib/api";

export default function AgentCustomers() {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [coll, setColl] = useState({ customer: null, amount: "" });

  const load = () => api.get("/agent/customers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const filtered = list.filter((c) => !q || (c.full_name || "").toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));

  const collect = async () => {
    if (!coll.amount || !coll.customer) return;
    try {
      await api.post("/agent/collections", {
        customer_id: coll.customer.id,
        policy_id: coll.customer.policy?.id || "n/a",
        amount: parseFloat(coll.amount),
        method: "cash",
      });
      toast.success("Collection recorded");
      setColl({ customer: null, amount: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="agent-customers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">My Customers</h1>
          <p className="text-sm text-slate-500">{list.length} total</p>
        </div>
        <button data-testid="goto-add-customer" onClick={() => nav("/agent/add-customer")} className="inline-flex items-center gap-2 bg-tenant-primary text-white px-4 py-2.5 rounded-xl text-sm font-bold tap-scale">
          <UserPlus className="w-4 h-4" /> Add Customer
        </button>
      </div>

      <div className="mt-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="agent-customer-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone" className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl outline-none focus:border-tenant-primary bg-white" />
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`agent-customer-${c.id}`} className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-tenant-primary grid place-items-center font-extrabold">{(c.full_name || "U").charAt(0)}</div>
              <div className="flex-1 min-w-0">
                <div className="font-extrabold tracking-tight truncate">{c.full_name}</div>
                <div className="text-[11px] text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" /> +91 {c.phone}</div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-slate-500">Saving</span>
              <span className="font-extrabold text-emerald-600">{fmtINR(c.saving_balance)}</span>
            </div>
            {c.policy && (
              <div className="text-[11px] text-slate-400 mt-1">Policy: {c.policy.policy_number} · Daily {fmtINR(c.policy.daily_amount)}</div>
            )}
            <button data-testid={`collect-${c.id}`} onClick={() => setColl({ customer: c, amount: String(c.policy?.daily_amount || 200) })} className="mt-3 w-full bg-emerald-50 text-emerald-700 py-2 rounded-xl font-bold text-sm tap-scale inline-flex items-center justify-center gap-1.5">
              <PlusCircle className="w-4 h-4" /> Record Collection
            </button>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center text-slate-400 text-sm py-12 col-span-full">No customers found.</div>}
      </div>

      {coll.customer && (
        <div data-testid="collect-modal" className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setColl({ customer: null, amount: "" })}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-extrabold text-lg">Daily Collection</div>
            <div className="text-sm text-slate-500">{coll.customer.full_name}</div>
            <label className="mt-3 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3">
              <IndianRupee className="w-5 h-5 text-slate-400" />
              <input data-testid="collect-amount" value={coll.amount} onChange={(e) => setColl({ ...coll, amount: e.target.value.replace(/[^\d.]/g, "") })} className="flex-1 outline-none text-xl font-extrabold" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => setColl({ customer: null, amount: "" })} className="py-3 rounded-xl border border-slate-200 font-bold">Cancel</button>
              <button data-testid="confirm-collect" onClick={collect} className="py-3 rounded-xl bg-emerald-600 text-white font-bold">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
